import React, { useEffect, useState } from 'react';
import { Image, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import classNames from 'classnames/bind';
import styles from './BlogListing.module.scss';
import { useRouter } from 'next/router';
import withStyles from 'lib/enhancers/withStyles';
import Button, { ButtonVariant } from 'components/helpers/Button/Button';
import {
  BlogConstants,
  BlogPostData,
  BlogPostsRequestByFilter,
  BlogListingProps,
  BlogTopicData,
} from 'models/blog-types';
import { getBlogPosts } from '../API';
import BlogTopicsList from '../BlogTopicsList';
import ShortDescriptionFieldWrapper from 'components/helpers/ShortDescriptionFieldWrapper';
import Icon from 'components/helpers/Icon';
import Loader, { LoaderVariant } from 'components/helpers/Loader/Loader';
import { getIdsFromArray, getLanguageBasedUrl, useDictionary } from 'lib/utils';
import BlogListingFilters from './BlogListingFilters';
import { filterPostsByTopic, getDatabaseName } from '../Functions';

const BlogListing = (props: BlogListingProps): React.JSX.Element => {
  const cx = classNames.bind(styles);
  const rootItemId = BlogConstants.BlogPostsRoot; // Pass 0 to not filter on any root-item - just tenant based filter will be applied
  const latestPostsLabel = useDictionary('Latest Posts');
  const sortedByLabel = useDictionary('Sorted by Latest');
  const nextLabel = useDictionary('Next');
  const previousLabel = useDictionary('Previous');
  const router = useRouter();
  const { sitecoreContext } = useSitecoreContext();
  const { fields } = props;
  const { blogAudience, blogsPerPage, hideFilters, filtersToShowOnLoad } = fields;
  const { language, pageState } = sitecoreContext;

  const databaseName = getDatabaseName(sitecoreContext);
  const showFilters = `${hideFilters?.value}` !== 'true' && `${hideFilters?.value}` !== '1';
  const [blogListingDataLoaded, setBlogListingDataLoaded] = useState<boolean>(false);
  const [blogListingData, setBlogListingData] = useState<BlogPostData[]>([]);
  const [blogListingInitialData, setBlogListingInitialData] = useState<BlogPostData[]>([]);
  const [filterTopics, setFilterTopics] = useState<BlogTopicData[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const isNormalPageMode = pageState === 'normal';

  const handleFilterChange = (topics: BlogTopicData[]) => {
    setCurrentPageIndex(0);
    const $posts = filterPostsByTopic(blogListingInitialData, topics);
    setFilterTopics(topics);
    setBlogListingData($posts);
  };

  useEffect(() => {
    const loadBlogPostsFlag = (!isNormalPageMode || router.isReady) && !blogListingDataLoaded;

    if (!isNormalPageMode) {
      console.log(
        `BlogListing - useEffect - isNormalPageMode: ${isNormalPageMode} isReady: ${router.isReady} blogListingDataLoaded: ${blogListingDataLoaded}`
      );
      console.log(`BlogListing - loadBlogPostsFlag: ${loadBlogPostsFlag}`);
    }

    if (loadBlogPostsFlag) {
      const blogPostsRequest: BlogPostsRequestByFilter = {
        language: language,
        blogAudience: getIdsFromArray(blogAudience),
        postsToShow: 0,
        rootItemId: `${rootItemId}`,
        database: databaseName,
      };

      getBlogPosts(blogPostsRequest).then((data) => {
        setBlogListingDataLoaded(true);
        const $posts = data?.posts || [];
        setBlogListingInitialData($posts);
        setBlogListingData($posts);
      });
    }
  });

  if (blogListingData?.length === 0) {
    return <></>;
  }

  const pageSize = +(blogsPerPage?.value || 10);
  const totalPages = Math.ceil(blogListingData?.length / pageSize);
  const startItemIndex = currentPageIndex === 0 ? currentPageIndex : currentPageIndex * pageSize;
  const endItemIndex = startItemIndex === 0 ? pageSize : startItemIndex + pageSize;
  const additionalClass = showFilters ? 'blog-listing__withfilters' : 'blog-listing__nofilters';

  return (
    <div className={cx('blog-listing', additionalClass)}>
      {!blogListingDataLoaded && <Loader variant={LoaderVariant.Spinner} className={cx('loader')} />}

      {blogListingDataLoaded && (
        <div className={cx('blog-listing-section-heading', 'beforeFilters')}>
          <h2>{latestPostsLabel.replace('{count}', `${blogListingData?.length}`)}</h2>
          <span>{sortedByLabel}</span>
        </div>
      )}

      {blogListingDataLoaded && showFilters && (
        <BlogListingFilters
          posts={blogListingInitialData}
          onFilterChangeCallback={handleFilterChange}
          filtersToShowOnLoad={+(filtersToShowOnLoad?.value || 5)}
        />
      )}

      <div className={cx('data')}>
        {blogListingDataLoaded && (
          <div className={cx('blog-listing-section-heading', 'afterFilters')}>
            <h2>{latestPostsLabel.replace('{count}', `${blogListingData?.length}`)}</h2>
            <span>{sortedByLabel}</span>
          </div>
        )}

        <div className={cx('filteredtopics')}>
          {blogListingDataLoaded && showFilters && filterTopics.length > 0 && (
            <BlogTopicsList blogTopicsData={filterTopics} language={language} allowRemove={true} />
          )}
        </div>

        <div className={cx('list')}>
          {blogListingDataLoaded &&
            blogListingData?.slice(startItemIndex, endItemIndex.valueOf()).map((item: BlogPostData, index: number) => (
              <div key={index} className={cx('blog-container')}>
                <div className={cx('blog-image-column')}>
                  {item.blogPhoto && (
                    <Image field={{ src: item.blogPhoto }} className={cx('blog-image-column__image')} />
                  )}
                </div>
                <div className={cx('blog-text-column')}>
                  <BlogTopicsList blogTopicsData={item.blogTopics} language={language} />
                  <div className={cx('blog-heading')}>
                    <a href={getLanguageBasedUrl(item.url, sitecoreContext.language)}>{item.blogHeading}</a>
                    <Icon variant="carat" />
                  </div>
                  <ShortDescriptionFieldWrapper
                    textField={{ value: item.blogShortDescription }}
                    stringLength={350}
                    className={cx('blog-summary')}
                  />
                </div>
              </div>
            ))}
        </div>
        {blogListingDataLoaded && totalPages > 1 && (
          <div className={cx('pagination-container')}>
            <Button
              className={cx('previous-button')}
              variant={ButtonVariant.Anchor}
              disabled={currentPageIndex == 0}
              onClick={() => setCurrentPageIndex(currentPageIndex - 1)}
            >
              <Icon variant="carat" /> {previousLabel}
            </Button>
            <div className={cx('pagination-numeric-button')}>
              {Array.from({ length: totalPages }, (_v, i) => (
                <Button
                  key={`posts-page-${i}`}
                  variant={ButtonVariant.Anchor}
                  onClick={() => setCurrentPageIndex(i)}
                  className={cx(`active-${i === currentPageIndex ? '1' : '0'}`)}
                >
                  {i + 1}
                </Button>
              ))}
            </div>
            <Button
              className={cx('next-button')}
              variant={ButtonVariant.Anchor}
              disabled={currentPageIndex >= totalPages - 1}
              onClick={() => setCurrentPageIndex(currentPageIndex + 1)}
            >
              {nextLabel} <Icon variant="carat" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default (withValidDatasourceCheck(), withStyles())(BlogListing);
